import type { QueryKey } from "@tanstack/vue-query";
import type { HydraCollection } from "@verbleif/lib/src/composables/Api/Type/hydra.interface";
import type { NotificationHydraItem } from "@verbleif/lib/src/composables/Api/Type/Notification/notification.interface";
import type { NotificationChannelSettingHydraItem, QuietHoursDateOverrideHydraItem, QuietHoursOverrideModeEnum, QuietHoursStrategyEnum } from "@verbleif/lib/src/composables/Api/Type/NotificationChannelSetting/notificationChannelSetting.interface";
import type { NotificationConditionDocument } from "@verbleif/lib/src/composables/Api/Type/NotificationRule/notificationCondition.interface";
import type { NotificationChannelEnum } from "@verbleif/lib/src/composables/Api/Type/NotificationRule/notificationRule.interface";
import type { NotificationSettingHydraItem } from "@verbleif/lib/src/composables/Api/Type/NotificationSetting/notificationSetting.interface";
import type { ScheduledDigestEntityScopeEnum, ScheduledDigestSubscriptionHydraItem } from "@verbleif/lib/src/composables/Api/Type/ScheduledDigestSubscription/scheduledDigestSubscription.interface";
import type { UserNotificationHydraItem, UserNotificationUnreadCount } from "@verbleif/lib/src/composables/Api/Type/UserNotification/userNotification.interface";
import type { ResourceScope } from "../../Query/resourceKeys";
import { mutationOptions } from "@tanstack/vue-query";
import { NotificationChannelStatusEnum } from "@verbleif/lib/src/composables/Api/Type/NotificationChannelSetting/notificationChannelSetting.interface";
import { getQueryClient } from "@verbleif/lib/src/composables/Query/queryClient";
import { api } from "../../Api/api";
import { deleteMutation, patchMutation, postMutation } from "../../Mutation/hydraMutations";
import { resourceKeys } from "../../Query/resourceKeys";
import { fetchAllPages } from "../../Query/useResourceAll";
import { notificationQueryKey } from "./notificationQuery";

export enum NotificationSilencePresetEnum {
  THIRTY_MINUTES = "30min",
  ONE_HOUR = "1h",
  FOUR_HOURS = "4h",
  UNTIL_TOMORROW = "tomorrow",
  UNTIL_NEXT_WEEK = "next_week",
  CUSTOM = "custom",
}

export const ALL_CHANNEL_SETTING_KEYS = [
  "inboxChannelSetting",
  "emailChannelSetting",
  "webPushChannelSetting",
  "mobileAppChannelSetting",
] as const;

export type ChannelSettingKey = typeof ALL_CHANNEL_SETTING_KEYS[number];

const CHANNEL_SILENCE_DEFAULTS: Record<ChannelSettingKey, NotificationChannelStatusEnum> = {
  inboxChannelSetting: NotificationChannelStatusEnum.PRESET_ALL,
  emailChannelSetting: NotificationChannelStatusEnum.DISABLED,
  webPushChannelSetting: NotificationChannelStatusEnum.PRESET_ALL,
  mobileAppChannelSetting: NotificationChannelStatusEnum.PRESET_ALL,
};

export interface SilenceNotificationChannelSettingInput {
  preset: NotificationSilencePresetEnum
  customUntil?: string | null
}

export interface NotificationSubscriptionInput {
  "@id"?: string
  "topic": string
  "sound": boolean
  "isImportant"?: boolean
  "conditions": NotificationConditionDocument[]
  "active": boolean
}

export interface QuietHoursWindowEmbeddedInput {
  "@id"?: string
  "recurrenceRule": string
  "startTime": string
  "endTime": string
  "enabled": boolean
}

export interface NotificationChannelSettingInput {
  "@id"?: string
  "status": NotificationChannelStatusEnum
  "sound": boolean
  "quietHoursStrategy"?: QuietHoursStrategyEnum
  "quietHoursAllowImportant"?: boolean
  "subscriptions"?: NotificationSubscriptionInput[]
  "quietHoursWindows"?: QuietHoursWindowEmbeddedInput[]
}

export interface CreateNotificationSettingInput {
  subscriber?: string | null
  location?: string | null
  client?: string | null
  permissionTemplateGroup?: string | null
  active?: boolean
  inboxChannelSetting?: NotificationChannelSettingInput | null
  emailChannelSetting?: NotificationChannelSettingInput | null
  webPushChannelSetting?: NotificationChannelSettingInput | null
  mobileAppChannelSetting?: NotificationChannelSettingInput | null
}

export interface UpdateNotificationSettingInput {
  active?: boolean
  inboxChannelSetting?: NotificationChannelSettingInput | null
  emailChannelSetting?: NotificationChannelSettingInput | null
  webPushChannelSetting?: NotificationChannelSettingInput | null
  mobileAppChannelSetting?: NotificationChannelSettingInput | null
}

export interface CreateQuietHoursDateOverrideInput {
  channelSetting: string
  date: string
  dateRangeEnd?: string | null
  mode?: QuietHoursOverrideModeEnum
  note?: string | null
}

export interface QuietHoursDateOverrideQuery {
  page?: number
  perPage?: number
}

export interface QuietHoursSummaryQuery {
  channel: number
  windowEndsAt: string
}

export interface CreateScheduledDigestSubscriptionInput {
  channelSetting: string
  entityScope: ScheduledDigestEntityScopeEnum
  enabled?: boolean
  sendTime: string
  daysOfWeek: number[]
}

export interface UpdateScheduledDigestSubscriptionInput {
  enabled?: boolean
  sendTime?: string
  daysOfWeek?: number[]
}

interface MarkNotificationAsReadVariables {
  notificationId: number
  readAt: string
  isV2: boolean
}

interface MarkAllInboxNotificationsReadVariables {
  userId: number
  /** The selected location or organisation; without one, every inbox item of the user is marked read. */
  scope: ResourceScope
}

type QuerySnapshot = Array<[QueryKey, unknown]>;

/** The inbox of a user (notifications v2): their user notifications, newest listed first. */
export function userNotificationsCollectionUrl(userId: number): string {
  return `/api/users/${userId}/user_notifications`;
}

/** One user notification; the same string as the item's `@id`, so mutations hit the item cache. */
export function userNotificationUrl(notificationId: number): string {
  return `/api/user_notifications/${notificationId}`;
}

const USER_NOTIFICATION_IRI_PATTERN = /^\/api\/user_notifications\/(\d+)$/;

/** The id of a user notification IRI (as pushes and e-mail links carry it), or null for anything else. */
export function userNotificationIdFromIri(iri: unknown): number | null {
  if (typeof iri !== "string") {
    return null;
  }
  const match = iri.match(USER_NOTIFICATION_IRI_PATTERN);
  return match ? Number(match[1]) : null;
}

export function userNotificationUnreadCountQueryKey(userId: number) {
  return [...resourceKeys.notifications.root, "userNotificationUnreadCount", userId] as const;
}

/** The unread inbox items of a user, in total and per location: the same number the app badge shows. */
export function userNotificationUnreadCountQueryOptions(userId: number) {
  return {
    queryKey: userNotificationUnreadCountQueryKey(userId),
    queryFn: async ({ signal }: { signal?: AbortSignal }): Promise<UserNotificationUnreadCount> => {
      const { data } = await api.get<UserNotificationUnreadCount>(`/api/users/${userId}/user_notification_unread_count`, { signal });
      return { total: data.total, locations: data.locations };
    },
  };
}

/**
 * The unread count of the selected location or organisation, matching what the inbox list shows and "mark all read"
 * clears; the whole inbox when nothing is selected. `clientOfLocation` resolves a location IRI to its client IRI.
 */
export function scopedUserNotificationUnreadCount(
  count: UserNotificationUnreadCount | undefined,
  scope: ResourceScope,
  clientOfLocation: (locationIri: string) => string | null,
): number {
  if (!count) {
    return 0;
  }
  if (scope.location) {
    return count.locations.find(entry => entry.location === scope.location)?.count ?? 0;
  }
  if (scope.client) {
    return count.locations
      .filter(entry => clientOfLocation(entry.location) === scope.client)
      .reduce((sum, entry) => sum + entry.count, 0);
  }
  return count.total;
}

/** Refetches the unread count of the v2 inbox; the Mercure update usually already brought the new number. */
export function refreshUserNotificationUnreadCount(): Promise<void> {
  return getQueryClient().invalidateQueries({ queryKey: [...resourceKeys.notifications.root, "userNotificationUnreadCount"] });
}

/**
 * Narrows an inbox collection request to the selected location or organisation (client), so the list and the
 * unread counter hold exactly what "mark all read" clears. Location and organisation mode never overlap; the
 * location is the narrower one and wins if both are set.
 */
export function applyInboxScopeFilter(params: URLSearchParams, scope: ResourceScope): void {
  if (scope.location) {
    params.set("location", scope.location);
    return;
  }
  if (scope.client) {
    params.set("location.client", scope.client);
  }
}

/** Body for mark_all_read: the API accepts one of `location` or `client` (as IRI), or neither for the whole inbox. */
export function inboxScopeBody(scope: ResourceScope): { location: string } | { client: string } | Record<string, never> {
  if (scope.location) {
    return { location: scope.location };
  }
  if (scope.client) {
    return { client: scope.client };
  }
  return {};
}

function isActiveSilence(value: string | null | undefined): value is string {
  if (!value) {
    return false;
  }
  return new Date(value).getTime() > Date.now();
}

/**
 * Returns the channel's `silencedUntil` only when it is still in the future.
 * Past timestamps mean delivery already resumed, so the channel shouldn't be
 * treated as muted in the UI even if the backend still echoes the value.
 */
export function getChannelActiveSilencedUntil(
  channel: NotificationChannelSettingHydraItem | null | undefined,
): string | null {
  if (!channel) {
    return null;
  }
  return isActiveSilence(channel.silencedUntil) ? channel.silencedUntil : null;
}

/**
 * Returns the earliest active `silencedUntil` across every channel on the
 * setting. When all channels are silenced together via `silenceAllChannels`
 * they share the same timestamp, so the value is suitable as a master
 * "muted until" label; use together with `areAllChannelsSilenced` to avoid
 * showing the label when only a subset of channels is silenced.
 */
export function getEarliestSilencedUntil(
  setting: NotificationSettingHydraItem | null,
): string | null {
  if (!setting) {
    return null;
  }

  let earliest: string | null = null;
  for (const key of ALL_CHANNEL_SETTING_KEYS) {
    const silencedUntil = getChannelActiveSilencedUntil(setting[key]);
    if (!silencedUntil) {
      continue;
    }
    if (earliest === null || silencedUntil < earliest) {
      earliest = silencedUntil;
    }
  }

  return earliest;
}

/**
 * Whether every notification channel on the setting exists and has a silence
 * timestamp in the future. The master "mute everything" toggle and "muted
 * until" label should only be shown when this is true. Otherwise the UI
 * misleads the user into thinking delivery is globally paused while one or
 * more channels still deliver.
 */
export function areAllChannelsSilenced(
  setting: NotificationSettingHydraItem | null,
): boolean {
  if (!setting) {
    return false;
  }

  for (const key of ALL_CHANNEL_SETTING_KEYS) {
    if (!getChannelActiveSilencedUntil(setting[key])) {
      return false;
    }
  }

  return true;
}

const notificationStatsQueryPrefix = [...resourceKeys.notifications.root, "stats"] as const;

function snapshotNotificationStatsQueries(): QuerySnapshot {
  return getQueryClient().getQueriesData({ queryKey: notificationStatsQueryPrefix });
}

function restoreQuerySnapshots(snapshots: QuerySnapshot): void {
  const client = getQueryClient();
  for (const [key, data] of snapshots) {
    client.setQueryData(key, data);
  }
}

/** Optimistically lowers the cached legacy unread counters by one. */
function decrementUnreadStats(): void {
  const client = getQueryClient();
  for (const [key, data] of client.getQueriesData({ queryKey: notificationStatsQueryPrefix })) {
    if (typeof data !== "number") {
      continue;
    }
    client.setQueryData(key, Math.max(0, data - 1));
  }
}

export async function loadNotificationCollectionPage(
  endpoint: string,
  params: URLSearchParams,
  signal?: AbortSignal,
): Promise<HydraCollection<NotificationHydraItem | UserNotificationHydraItem>> {
  const { data } = await api.get<HydraCollection<NotificationHydraItem | UserNotificationHydraItem>>(endpoint, {
    params,
    signal,
  });
  return data;
}

export function notificationSettingsQueryOptions(scope: ResourceScope, scopeIri: string) {
  return {
    queryKey: notificationQueryKey(scope, "getNotificationSettings", scopeIri),
    queryFn: async ({ signal }: { signal?: AbortSignal }) => {
      const { data } = await api.get<HydraCollection<NotificationSettingHydraItem>>(
        `${scopeIri}/notification_settings`,
        { signal },
      );
      return data.member;
    },
  };
}

export function globalNotificationSettingsQueryOptions(scope: ResourceScope) {
  return {
    queryKey: notificationQueryKey(scope, "getGlobalNotificationSettings"),
    queryFn: async ({ signal }: { signal?: AbortSignal }) => {
      const { data } = await api.get<HydraCollection<NotificationSettingHydraItem>>(
        "/api/notification_settings",
        { signal },
      );
      return data.member;
    },
  };
}

/**
 * Fetches the effective notification setting for a user at a location, walking
 * the inheritance chain (User → Role → Location → Client → Global) on the
 * server. Each channel returned carries a `source` field indicating which level
 * of the chain produced it; when no setting exists at any level the source is
 * `none` and the channel's setting payload is `null`.
 */
export function effectiveNotificationSettingQueryOptions(
  scope: ResourceScope,
  userIri: string,
  locationIri: string,
) {
  const userId = userIri.split("/").pop();
  const locationId = locationIri.split("/").pop();
  return {
    queryKey: notificationQueryKey(scope, "getEffectiveNotificationSetting", userIri, locationIri),
    queryFn: async ({ signal }: { signal?: AbortSignal }): Promise<NotificationSettingHydraItem | null> => {
      if (!userId || !locationId) {
        return null;
      }
      const { data } = await api.get<NotificationSettingHydraItem>(
        `/api/users/${userId}/locations/${locationId}/effective_notification_setting`,
        { signal },
      );
      return data;
    },
  };
}

/** The effective notification setting of a user at every location the user has access to, one item per location. */
export function effectiveNotificationSettingsPerLocationQueryOptions(scope: ResourceScope, userIri: string) {
  const userId = userIri.split("/").pop();
  return {
    queryKey: notificationQueryKey(scope, "getEffectiveNotificationSettingsPerLocation", userIri),
    queryFn: async ({ signal }: { signal?: AbortSignal }): Promise<NotificationSettingHydraItem[]> => {
      if (!userId) {
        return [];
      }
      const { data } = await api.get<HydraCollection<NotificationSettingHydraItem>>(
        `/api/users/${userId}/effective_notification_settings`,
        { signal },
      );
      return data.member;
    },
  };
}

export function quietHoursDateOverridesPaginatedQueryOptions(
  scope: ResourceScope,
  channelSettingIri: string,
  query: QuietHoursDateOverrideQuery = {},
) {
  const page = query.page ?? 1;
  const perPage = query.perPage ?? 20;
  return {
    queryKey: notificationQueryKey(scope, "getQuietHoursDateOverridesPaginated", channelSettingIri, page, perPage),
    queryFn: async ({ signal }: { signal?: AbortSignal }) => {
      const { data } = await api.get<HydraCollection<QuietHoursDateOverrideHydraItem>>(
        `${channelSettingIri}/quiet_hours_date_overrides`,
        {
          params: { page, perPage },
          signal,
        },
      );
      return data;
    },
  };
}

export function quietHoursSummaryQueryOptions(
  scope: ResourceScope,
  userId: number,
  query: QuietHoursSummaryQuery,
) {
  return {
    queryKey: notificationQueryKey(scope, "getQuietHoursSummary", userId, query.channel, query.windowEndsAt),
    // The items quiet hours held back for one summary: the inbox narrowed to the deliveries deferred to that window.
    queryFn: ({ signal }: { signal?: AbortSignal }) => fetchAllPages<UserNotificationHydraItem>({
      url: userNotificationsCollectionUrl(userId),
      initialParams: new URLSearchParams({
        "quietHoursWindow[channel]": String(query.channel),
        "quietHoursWindow[windowEndsAt]": query.windowEndsAt,
      }),
      signal,
    }),
  };
}

/**
 * Returns the (at most one) digest subscription that exists for the given
 * channel-setting + entity-scope tuple. The collection endpoint always
 * returns a Hydra collection so the caller picks the first member.
 */
export function digestForChannelEntityQueryOptions(
  scope: ResourceScope,
  channelSettingIri: string,
  entityScope: ScheduledDigestEntityScopeEnum,
) {
  return {
    queryKey: notificationQueryKey(scope, "getDigestForChannelEntity", channelSettingIri, entityScope),
    queryFn: async ({ signal }: { signal?: AbortSignal }) => {
      const { data } = await api.get<HydraCollection<ScheduledDigestSubscriptionHydraItem>>(
        `${channelSettingIri}/scheduled_digest_subscriptions`,
        { params: { entityScope }, signal },
      );
      return data.member[0] ?? null;
    },
  };
}

/**
 * Unread counter of the legacy notifications, which count over every location. The v2 inbox has its own count
 * endpoint: {@link userNotificationUnreadCountQueryOptions}.
 */
export function unreadNotificationsCountQueryOptions(
  scope: ResourceScope,
  endpoint: string,
  paramsKey: string,
) {
  return {
    queryKey: resourceKeys.notifications.stats(scope, paramsKey),
    queryFn: async ({ signal }: { signal?: AbortSignal }) => {
      const params = new URLSearchParams({
        "perPage": "1",
        "page": "1",
        "exists[readAt]": "false",
        "active": "true",
      });
      const { data } = await api.get<HydraCollection<unknown>>(endpoint, {
        params,
        signal,
      });
      return data.totalItems;
    },
  };
}

export function unreadNotificationsByEntityQueryOptions(
  scope: ResourceScope,
  endpoint: string,
  params: URLSearchParams,
) {
  return {
    queryKey: notificationQueryKey(scope, "unreadByEntity", endpoint, params.toString()),
    staleTime: 0,
    queryFn: async ({ signal }: { signal?: AbortSignal }) => {
      const { data } = await api.get<HydraCollection<NotificationHydraItem | UserNotificationHydraItem>>(endpoint, {
        params,
        signal,
      });
      return data;
    },
  };
}

export function createNotificationSettingMutation() {
  return mutationOptions({
    mutationKey: ["notifications", "createSetting"],
    mutationFn: async (data: CreateNotificationSettingInput) => {
      const response = await api.post<NotificationSettingHydraItem>("/api/notification_settings", data);
      return response.data;
    },
    onSuccess: async () => {
      await getQueryClient().invalidateQueries({ queryKey: resourceKeys.notifications.root });
    },
  });
}

export function updateNotificationSettingMutation() {
  return mutationOptions({
    mutationKey: ["notifications", "updateSetting"],
    mutationFn: async ({ settingIri, data }: { settingIri: string, data: UpdateNotificationSettingInput }) => {
      const response = await api.patch<NotificationSettingHydraItem>(settingIri, data);
      return response.data;
    },
    onSuccess: async () => {
      await getQueryClient().invalidateQueries({ queryKey: resourceKeys.notifications.root });
    },
  });
}

export function deleteNotificationSettingMutation() {
  return deleteMutation({
    mutationKey: ["notifications", "deleteSetting"],
    invalidates: [resourceKeys.notifications],
  });
}

export function unsilenceChannelSettingMutation() {
  return mutationOptions({
    mutationKey: ["notifications", "unsilenceChannel"],
    mutationFn: async (channelSettingIri: string) => {
      const response = await api.post<NotificationChannelSettingHydraItem>(
        `${channelSettingIri}/unsilence`,
      );
      return response.data;
    },
    onSuccess: async () => {
      await getQueryClient().invalidateQueries({ queryKey: resourceKeys.notifications.root });
    },
  });
}

/** The API stores a window's times as a datetime; only the wall-clock part is written back. */
function windowTime(value: string): string {
  const match = /(\d{2}:\d{2})(:\d{2})?/.exec(value);
  if (!match) {
    throw new Error(`Quiet hours window time "${value}" has no time of day.`);
  }
  return `${match[1]}${match[2] ?? ":00"}`;
}

/**
 * The "Niet storen" schedule of an inherited "Aangepast" channel, as new windows of the user's own copy. The API keeps
 * quiet hours only on "Aangepast", so a preset has none to copy.
 */
export function inheritedQuietHoursInput(
  inherited: NotificationChannelSettingHydraItem,
): Pick<NotificationChannelSettingInput, "quietHoursStrategy" | "quietHoursAllowImportant" | "quietHoursWindows"> {
  return {
    quietHoursStrategy: inherited.quietHoursStrategy,
    quietHoursAllowImportant: inherited.quietHoursAllowImportant,
    quietHoursWindows: (inherited.quietHoursWindows ?? []).map(window => ({
      recurrenceRule: window.recurrenceRule,
      startTime: windowTime(window.startTime),
      endTime: windowTime(window.endTime),
      enabled: window.enabled,
    })),
  };
}

/**
 * The channel a missing channel setting starts from: a copy of what the user gets there now (the inherited setting
 * with its topics and its "Niet storen" schedule), so creating it changes nothing until the user changes it. A channel
 * without any setting sends nothing, so it starts switched off.
 */
export function inheritedChannelSettingInput(
  inherited: NotificationChannelSettingHydraItem | null | undefined,
): NotificationChannelSettingInput {
  if (!inherited) {
    return { status: NotificationChannelStatusEnum.DISABLED, sound: true };
  }
  const input: NotificationChannelSettingInput = { status: inherited.status, sound: inherited.sound };
  if (inherited.status !== NotificationChannelStatusEnum.CUSTOM) {
    return input;
  }
  return {
    ...input,
    ...inheritedQuietHoursInput(inherited),
    subscriptions: (inherited.subscriptions ?? []).map(subscription => ({
      topic: subscription.topic,
      sound: subscription.sound,
      isImportant: subscription.isImportant,
      conditions: subscription.conditions,
      active: subscription.active,
    })),
  };
}

/**
 * Silences every channel on the notification setting so that muting affects
 * all delivery channels (inbox, email, web push, mobile app) instead of only inbox.
 *
 * Missing channel settings are created first so the silence timestamp can be applied to each one, then the silence
 * endpoint is called for all channels in parallel. Pass `inherited` (the user's effective setting at the location)
 * to create them as a copy of what the user gets there now; without it they get a fixed default status.
 */
export function silenceAllChannelsMutation() {
  return mutationOptions({
    mutationKey: ["notifications", "silenceAllChannels"],
    mutationFn: async ({
      setting,
      input,
      inherited,
    }: {
      setting: NotificationSettingHydraItem
      input: SilenceNotificationChannelSettingInput
      inherited?: NotificationSettingHydraItem | null
    }) => {
      if (!setting["@id"]) {
        throw new Error("Notification setting ID not found");
      }

      const missingChannels: UpdateNotificationSettingInput = {};
      for (const key of ALL_CHANNEL_SETTING_KEYS) {
        if (setting[key]) {
          continue;
        }
        missingChannels[key] = inherited === undefined
          ? { status: CHANNEL_SILENCE_DEFAULTS[key], sound: true }
          : inheritedChannelSettingInput(inherited?.[key]);
      }

      let updatedSetting = setting;
      if (Object.keys(missingChannels).length > 0) {
        const response = await api.patch<NotificationSettingHydraItem>(setting["@id"], missingChannels);
        updatedSetting = response.data;
      }

      const channelIris = ALL_CHANNEL_SETTING_KEYS
        .map(key => updatedSetting[key]?.["@id"])
        .filter((iri): iri is string => Boolean(iri));

      if (channelIris.length === 0) {
        throw new Error("No channel settings available to silence");
      }

      await Promise.all(channelIris.map(async (iri) => {
        await api.post(`${iri}/silence`, input);
      }));

      return updatedSetting;
    },
    onSettled: async () => {
      await getQueryClient().invalidateQueries({ queryKey: resourceKeys.notifications.root });
    },
  });
}

/**
 * Unsilences every channel on the notification setting that currently has a
 * silence timestamp set, restoring notification delivery across all channels.
 */
export function unsilenceAllChannelsMutation() {
  return mutationOptions({
    mutationKey: ["notifications", "unsilenceAllChannels"],
    mutationFn: async (setting: NotificationSettingHydraItem) => {
      const channelIris = ALL_CHANNEL_SETTING_KEYS
        .map(key => setting[key])
        .filter(
          (channelSetting): channelSetting is NotificationChannelSettingHydraItem =>
            !!channelSetting?.silencedUntil && !!channelSetting["@id"],
        )
        .map(channelSetting => channelSetting["@id"]!);

      if (channelIris.length === 0) {
        return;
      }

      await Promise.all(channelIris.map(async (iri) => {
        await api.post(`${iri}/unsilence`);
      }));
    },
    onSettled: async () => {
      await getQueryClient().invalidateQueries({ queryKey: resourceKeys.notifications.root });
    },
  });
}

export function createQuietHoursDateOverrideMutation() {
  return mutationOptions({
    mutationKey: ["notifications", "createQuietHoursDateOverride"],
    mutationFn: async (data: CreateQuietHoursDateOverrideInput) => {
      const response = await api.post<QuietHoursDateOverrideHydraItem>(
        "/api/quiet_hours_date_overrides",
        data,
      );
      return response.data;
    },
    onSuccess: async () => {
      await getQueryClient().invalidateQueries({ queryKey: resourceKeys.notifications.root });
    },
  });
}

export function deleteQuietHoursDateOverrideMutation() {
  return deleteMutation({
    mutationKey: ["notifications", "deleteQuietHoursDateOverride"],
    invalidates: [resourceKeys.notifications],
  });
}

/**
 * Force-flushes any deferred quiet hours summary deliveries for the user that
 * still have a future windowEndsAt, dispatching the summary push immediately.
 * Called when the user opens the app early or explicitly asks to "see now".
 */
export function flushQuietHoursSummaryMutation() {
  return mutationOptions({
    mutationKey: ["notifications", "flushQuietHoursSummary"],
    mutationFn: async (userId: number) => {
      await api.post(`/api/users/${userId}/quiet_hours_summary/flush`, {});
    },
    onSuccess: async () => {
      await getQueryClient().invalidateQueries({ queryKey: resourceKeys.notifications.root });
    },
  });
}

export function createDigestMutation() {
  return mutationOptions({
    mutationKey: ["notifications", "createDigest"],
    mutationFn: async (data: CreateScheduledDigestSubscriptionInput) => {
      const response = await api.post<ScheduledDigestSubscriptionHydraItem>(
        "/api/scheduled_digest_subscriptions",
        {
          ...data,
          enabled: data.enabled ?? true,
        },
      );
      return response.data;
    },
    onSuccess: async () => {
      await getQueryClient().invalidateQueries({ queryKey: resourceKeys.notifications.root });
    },
  });
}

export function patchDigestMutation() {
  return mutationOptions({
    mutationKey: ["notifications", "patchDigest"],
    mutationFn: async ({
      digestIri,
      data,
    }: {
      digestIri: string
      data: UpdateScheduledDigestSubscriptionInput
    }) => {
      const response = await api.patch<ScheduledDigestSubscriptionHydraItem>(digestIri, data);
      return response.data;
    },
    onSuccess: async () => {
      await getQueryClient().invalidateQueries({ queryKey: resourceKeys.notifications.root });
    },
  });
}

export function deleteDigestMutation() {
  return deleteMutation({
    mutationKey: ["notifications", "deleteDigest"],
    invalidates: [resourceKeys.notifications],
  });
}

export function markNotificationAsReadMutation() {
  return mutationOptions({
    mutationKey: ["notifications", "markAsRead"],
    mutationFn: async ({ notificationId, readAt, isV2 }: MarkNotificationAsReadVariables) => {
      if (isV2) {
        const response = await api.patch(userNotificationUrl(notificationId), { readAt });
        return response.data;
      }
      const response = await api.post(`/api/legacy_notifications/${notificationId}/read`, { readAt });
      return response.data;
    },
    onMutate: async () => {
      const client = getQueryClient();
      await client.cancelQueries({ queryKey: notificationStatsQueryPrefix });
      const snapshots = snapshotNotificationStatsQueries();
      decrementUnreadStats();
      return { snapshots };
    },
    onError: (_error, _variables, context) => {
      if (!context?.snapshots) {
        return;
      }
      restoreQuerySnapshots(context.snapshots);
    },
  });
}

/**
 * Marks the unread v2 inbox items of the selected location or organisation read (all of them without a scope). The
 * unread count follows from the Mercure update or {@link refreshUserNotificationUnreadCount}, left to the caller.
 */
export function markAllInboxNotificationsReadMutation() {
  return mutationOptions({
    mutationKey: ["notifications", "markAllRead"],
    mutationFn: async ({ userId, scope }: MarkAllInboxNotificationsReadVariables) => {
      await api.post(`${userNotificationsCollectionUrl(userId)}/mark_all_read`, inboxScopeBody(scope));
    },
  });
}

/** One "Niet storen" schedule: ISO weekdays (1 = Monday) and "HH:MM" times in the user's time zone. */
export interface QuietHoursScheduleDocument {
  daysOfWeek: number[]
  startTime: string
  endTime: string
  enabled: boolean
}

/** One of the "Stille dagen": a "YYYY-MM-DD" date and an optional name. */
export interface QuietDayDocument {
  date: string
  name: string | null
}

/** "Niet storen" of the signed-in user at one location, for every channel at once. */
export interface MyNotificationQuietHours {
  location: string
  strategy: QuietHoursStrategyEnum
  allowImportant: boolean
  schedules: QuietHoursScheduleDocument[]
  quietDays: QuietDayDocument[]
  /** False until the user saved it; until then the quiet hours per channel apply. */
  stored: boolean
  /** The channels whose own quiet hours apply while nothing is stored. */
  channelsWithOwnQuietHours: string[]
  updatedAt: string | null
}

export type MyNotificationQuietHoursInput = Pick<MyNotificationQuietHours, "location" | "strategy" | "allowImportant" | "schedules" | "quietDays">;

export function myNotificationQuietHoursQueryOptions(scope: ResourceScope, locationIri: string) {
  return {
    queryKey: notificationQueryKey(scope, "getMyNotificationQuietHours", locationIri),
    queryFn: async ({ signal }: { signal?: AbortSignal }): Promise<MyNotificationQuietHours> => {
      const { data } = await api.get<MyNotificationQuietHours>("/api/me/notification_quiet_hours", {
        params: { location: locationIri },
        signal,
      });
      return data;
    },
  };
}

export function saveMyNotificationQuietHoursMutation() {
  return mutationOptions({
    mutationKey: ["notifications", "saveMyQuietHours"],
    mutationFn: async (input: MyNotificationQuietHoursInput) => {
      const { data } = await api.post<MyNotificationQuietHours>("/api/me/notification_quiet_hours", input);
      return data;
    },
    onSuccess: async () => {
      await getQueryClient().invalidateQueries({ queryKey: resourceKeys.notifications.root });
    },
  });
}

export interface SendTestNotificationInput {
  channel: NotificationChannelEnum
  location: string
}

/** "Testmelding sturen": one test notification over one channel; 422 when the channel cannot reach the user. */
export function sendTestNotificationMutation() {
  return mutationOptions({
    mutationKey: ["notifications", "sendTest"],
    mutationFn: async (input: SendTestNotificationInput) => {
      await api.post("/api/me/notification_channel_tests", input);
    },
  });
}

export function sendTestMobileNotificationMutation() {
  return postMutation({
    mutationKey: ["notifications", "sendTestMobile"],
  });
}

export function updateLegacyNotificationSubscriptionMutation() {
  return patchMutation({
    mutationKey: ["notifications", "updateLegacySubscription"],
  });
}

export function createLegacyNotificationSubscriptionMutation() {
  return postMutation({
    mutationKey: ["notifications", "createLegacySubscription"],
  });
}
